import { isAxiosError } from "axios";
import axiosInstance from "./axiosIntercept";
import { DEFAULT_BILLING_COUNTRY_CODE } from "../utils/localisedBilling";

const stripeRequestConfig = () => ({
  withCredentials: true,
  headers: {
    "api-key": process.env.REACT_APP_API_KEY,
    "Content-Type": "application/json",
  },
});

export interface CreatePaymentLinkPayload {
  accountUuid: string;
  countryCode: string;
}

export interface CreatePaymentLinkResponse {
  success: boolean;
  monthly: string;
  yearly: string;
  countryCode: string;
  currencyCode: string;
  isDefault: boolean;
}

export interface PreviewCostPayload {
  accountUuid: string;
  countryCode: string;
}

export interface PreviewCostData {
  subscriptionEndsAt: string;
  billingCycle: string;
  currency: string;
  proratedTotalAmount: number;
  countryCode?: string;
  currencyCode?: string;
  isDefault?: boolean;
}

export interface PreviewCostResponse {
  success: boolean;
  data?: PreviewCostData;
  message?: string;
}

export interface LocalisedPricingQuotePayload {
  accountUuid: string;
  countryCode: string;
}

export interface LocalisedPricingQuotePeriod {
  base: number;
  extraLocation: number;
  extraLocationsTotal: number;
  total: number;
}

export interface LocalisedPricingQuote {
  success: boolean;
  countryCode: string;
  currencyCode: string;
  isDefault: boolean;
  totalLocations: number;
  includedLocations: number;
  freeLocationsCount: number;
  extraLocations: number;
  monthly: LocalisedPricingQuotePeriod;
  yearly: LocalisedPricingQuotePeriod;
}

export interface LocalisedPricingQuoteResponse {
  success: boolean;
  data?: LocalisedPricingQuote;
  message?: string;
}

const sanitizeCountryCode = (countryCode: string | undefined): string => {
  const trimmed = countryCode?.trim().toUpperCase();
  return trimmed && /^[A-Z]{2}$/.test(trimmed)
    ? trimmed
    : DEFAULT_BILLING_COUNTRY_CODE;
};

/**
 * Monthly + yearly Stripe Payment Links using Prices for the given country.
 * Falls back to USD Configuration Prices server-side if the package is missing.
 * @param {CreatePaymentLinkPayload} payload - Account and ISO2 country for checkout Prices.
 * @returns {Promise<CreatePaymentLinkResponse>} Monthly and yearly payment-link URLs.
 */
export const createPaymentLink = async (
  payload: CreatePaymentLinkPayload,
): Promise<CreatePaymentLinkResponse> => {
  const accountUuid = payload.accountUuid.trim();
  const countryCode = sanitizeCountryCode(payload.countryCode);
  const response = await axiosInstance.post<CreatePaymentLinkResponse>(
    `${process.env.REACT_APP_API_URL}v2/stripe/create-payment-link`,
    { accountUuid, countryCode },
    stripeRequestConfig(),
  );
  return response.data;
};

/**
 * Prorated extra-location cost for the rest of the billing period, in the
 * country's currency (USD fallback when the package is missing).
 * @param {PreviewCostPayload} payload - Account and ISO2 country for the extra-location Price.
 * @returns {Promise<PreviewCostResponse>} Success wrapper with prorated amount, or a failure message.
 */
export const previewCost = async (
  payload: PreviewCostPayload,
): Promise<PreviewCostResponse> => {
  try {
    const accountUuid = payload.accountUuid.trim();
    const countryCode = sanitizeCountryCode(payload.countryCode);
    const response = await axiosInstance.post<PreviewCostData>(
      `${process.env.REACT_APP_API_URL}v2/stripe/preview-cost`,
      { accountUuid, countryCode },
      stripeRequestConfig(),
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error fetching subscription preview:", error);
    if (isAxiosError(error) && error.response) {
      return {
        success: false,
        message:
          error.response.data?.message ||
          "Failed to fetch subscription preview.",
      };
    }
    return {
      success: false,
      message:
        "An unexpected error occurred while fetching subscription preview.",
    };
  }
};

/**
 * Localised Pro quote for the upgrade popup: location counts plus monthly and
 * yearly totals in display/major units for the country's currency (USD
 * Configuration fallback when no package exists).
 * @param {LocalisedPricingQuotePayload} payload - Account and ISO2 country.
 * @returns {Promise<LocalisedPricingQuoteResponse>} Success wrapper with quote, or a failure message.
 */
export const quoteLocalisedPricing = async (
  payload: LocalisedPricingQuotePayload,
): Promise<LocalisedPricingQuoteResponse> => {
  try {
    const accountUuid = payload.accountUuid.trim();
    const countryCode = sanitizeCountryCode(payload.countryCode);
    const response = await axiosInstance.post<LocalisedPricingQuote>(
      `${process.env.REACT_APP_API_URL}v1/stripe/localised-pricing/quote`,
      { accountUuid, countryCode },
      stripeRequestConfig(),
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error fetching localised pricing quote:", error);
    if (isAxiosError(error) && error.response) {
      const status = error.response.status;
      const apiMessage = error.response.data?.message;
      if (status === 400) {
        return {
          success: false,
          message: apiMessage || "Invalid country code.",
        };
      }
      if (status === 404) {
        return {
          success: false,
          message: apiMessage || "Account not found.",
        };
      }
      return {
        success: false,
        message: apiMessage || "Failed to fetch localised pricing.",
      };
    }
    return {
      success: false,
      message: "An unexpected error occurred while fetching localised pricing.",
    };
  }
};
